import React from 'react';
import { NavLink } from 'react-router-dom';

import { usePublicSitePath } from '../../../pages/public/publicSitePaths';
import { PublicCopy, PublicLanguage } from '../../../pages/public/types';
import { WebsiteSettings } from '../WebsiteTemplate';
import LanguageToggle from './LanguageToggle';

interface PublicHeaderProps {
  settings: WebsiteSettings;
  copy: PublicCopy;
  language: PublicLanguage;
  onLanguageChange: (language: PublicLanguage) => void;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `text-sm font-medium transition-colors hover:text-white ${isActive ? 'text-white font-semibold' : 'text-gray-300'}`;

const PublicHeader: React.FC<PublicHeaderProps> = ({
  settings,
  copy,
  language,
  onLanguageChange,
}) => {
  const phoneDisplay = settings.contact.phone || '';
  const phoneHref = phoneDisplay ? `tel:${phoneDisplay.replace(/[^+\d]/g, '')}` : '';
  const dealerName = settings.dealershipName || 'Dealership';
  const publicSitePath = usePublicSitePath();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isUabSite =
    hostname === 'unitedautobrokers.online' || hostname === 'www.unitedautobrokers.online';
  const fallbackLogo =
    isUabSite || /united auto brokers/i.test(dealerName)
      ? '/images/united-auto-brokers-logo.png'
      : '/images/logo512.png';

  return (
    <header
      className="sticky top-0 z-50 shadow-lg"
      style={{ backgroundColor: '#0A1628', borderBottom: '2px solid #1565C0' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
        {/* Logo */}
        <NavLink to={publicSitePath('/')} className="flex-shrink-0">
          <img
            src={settings.branding.logoUrl || fallbackLogo}
            alt={dealerName}
            className="h-20 w-auto"
          />
        </NavLink>

        {/* Nav */}
        <nav className="hidden md:flex items-center gap-6">
          <NavLink to={publicSitePath('/')} className={navLinkClass} end>
            {copy.navigation.home}
          </NavLink>
          <NavLink to={publicSitePath('/inventory')} className={navLinkClass}>
            {copy.navigation.inventory}
          </NavLink>
          <NavLink to={publicSitePath('/blog')} className={navLinkClass}>
            {copy.navigation.blog}
          </NavLink>
          <NavLink to={publicSitePath('/financing')} className={navLinkClass}>
            {copy.navigation.financing}
          </NavLink>
          <NavLink to={publicSitePath('/contact')} className={navLinkClass}>
            {copy.navigation.contact}
          </NavLink>
        </nav>

        {/* Right: lang toggle + my account + call CTA */}
        <div className="flex items-center gap-3">
          <LanguageToggle
            language={language}
            onChange={onLanguageChange}
            label={copy.navigation.languageLabel}
          />
          <NavLink
            to={publicSitePath('/customer/login')}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-gray-500 hover:border-white px-3 py-1.5 text-sm font-medium text-gray-300 hover:text-white transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clipRule="evenodd"
              />
            </svg>
            <span>{copy.navigation.myAccount}</span>
          </NavLink>
          {phoneDisplay && (
            <a
              href={phoneHref}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-bold text-white transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.05em' }}
            >
              <span>📞</span>
              <span>{phoneDisplay}</span>
            </a>
          )}
        </div>
      </div>
    </header>
  );
};

export default PublicHeader;
